import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import Button from 'components/Button/Button';
import Chips from 'components/Chips/Chips';
import ContentFooter from 'components/ContentFooter/ContentFooter';
import FormGroup from 'components/FormGroup/FormGroup';
import InfoRow from 'components/InfoRow/InfoRow';
import Panel from 'components/Panel/Panel';
import PanelHeader from 'components/PanelHeader/PanelHeader';
import PaymentHistoryItem from 'components/PaymentHistoryItem/PaymentHistoryItem';
import SubscriptionCard from 'components/SubscriptionCard/SubscriptionCard';

import EditClient from './EditClient';
import EditSubscription from './EditSubscription';

import Edit24 from 'icons/Edit24';

import useApi from 'utils/api';
import { useAuth } from 'utils/auth';
import declOfNum from 'utils/declOfNum';
import { formatDate } from 'utils/formatAmount';
import { actions } from 'utils/permissions';

import styles from './client-page.scss';

const paymentTypes = [
    { value: 'all', label: 'Все платежи' },
    { value: 'subscription', label: 'Оплата подписки' },
];

const subscriptionTypes = [
    { value: 'active', label: 'Активные' },
    { value: 'all', label: 'Все' },
];

const SUB_PANEL_WIDTH = 400;

export default function ClientPage() {
    const auth = useAuth();
    const api = useApi();
    const { clientId } = useParams();
    const location = useLocation();
    const { t } = useTranslation();

    const [client, setClient] = React.useState(getClientDataFromLocation());

    const [paymentType, setPaymentType] = React.useState('all');
    const [subscriptionType, setSubscriptionType] = React.useState('active');

    const [edit, setEdit] = React.useState(false);
    const [editSubscription, setEditSubscription] = React.useState(null);

    /**
     * @typedef PaymentsResult
     * @type {object}
     * @property {Array} payments - Array of payments.
     * @property {number} total - Total number of payments.
     * @property {number} page - Current page.
     * @property {number} page_size - Number of payments per page.
     */

    /**
     * @typedef PaymentsQueryResult
     * @type {object}
     * @property {PaymentsResult} data - Payments response result.
     * @property {boolean} isSuccess - When query is successfully resolved.
     */

    /** @type {PaymentsQueryResult} */
    const paymentsQuery = useQuery({
        queryKey: ['payments', 'client-' + clientId],
        queryFn: getPayments,
        staleTime: 1000 * 10,
        retry: 0,
    });

    const subscriptionsQuery = useQuery({
        queryKey: ['subscriptions', 'client-' + clientId],
        queryFn: getSubscriptions,
        staleTime: 1000 * 10,
        retry: 0,
    });

    async function getPayments() {
        const { company } = auth;

        if (!company) return;

        return await api.getClientPayments(company.id, Number(clientId));
    }

    async function getSubscriptions() {
        const { company } = auth;

        if (!company) return;

        return await api.getClientSubscriptions(company.id, Number(clientId));
    }

    function handleEditClick() {
        setEdit(true);
        setEditSubscription(null);
    }

    function handleEditBackClick() {
        setEdit(false);
        setEditSubscription(null);
    }

    function handlePaymentTypeChange(value) {
        setPaymentType(value);
    }

    function handleSubscriptionClick(id) {
        setEditSubscription(id);
        setEdit(false);
    }

    function handleSubscriptionTypeChange(value) {
        setSubscriptionType(value);
    }

    function getClientDataFromLocation() {
        return location.state?.clientData;
    }

    function updateClientData(updatedData) {
        setClient((prev) => ({ ...prev, ...updatedData }));
    }

    function editUserAction() {
        if (auth.hasPermission(actions.EDIT_USER)) {
            return <Button level={'secondary'} icon={<Edit24 />} text="Редактировать" onClick={handleEditClick} />;
        }

        return null;
    }

    function getSubscriptionToEdit() {
        return subscriptionsQuery.data?.client_subscriptions.find((sub) => sub.id === editSubscription);
    }

    function prepareHistoryToRender() {
        const paymentsHistory = paymentsQuery.data?.payments;

        if (paymentsHistory && Array.isArray(paymentsHistory)) {
            if (paymentType === 'subscription') {
                return paymentsHistory.filter((item) => item.is_recurrent);
            }

            return paymentsHistory;
        }

        return [];
    }

    function prepareSubscriptionsToRender() {
        let subscriptions = subscriptionsQuery.data?.client_subscriptions || [];
        subscriptions = subscriptions.filter((s) => s.status !== null);

        if (subscriptionType === 'active') {
            subscriptions = subscriptions.filter((s) => s.status === 'ACTIVE');
        }

        return subscriptions.map((sub) => {
            return (
                <SubscriptionCard
                    key={sub.id}
                    onClick={handleSubscriptionClick}
                    title={sub.title}
                    id={sub.id}
                    selected={editSubscription === sub.id}
                    cost={sub.regular_amount}
                    once_per_days={sub.once_per_days}
                    paused_at={sub.paused_at}
                    status={sub.status}
                />
            );
        });
    }

    const subscriptionsToRender = prepareSubscriptionsToRender();

    const payments = prepareHistoryToRender().map((payment) => {
        return <PaymentHistoryItem key={payment.id} payment={payment} />;
    });

    const paymentsCountWord = payments.length > 0 ? payments.length : 'Нет';

    return (
        <>
            <Panel fill={true} header={<PanelHeader title={client.name} back={true} after={editUserAction()} />}>
                <section className={styles.about}>
                    <FormGroup title={t('clientPage.about')}>
                        <div className={styles.inforows}>
                            <InfoRow label={t('clientPage.email')}>{client.email}</InfoRow>
                            <InfoRow label={t('clientPage.phone')}>{client.phone_number}</InfoRow>
                            {client.masked_pan && <InfoRow label={t('clientPage.card')}>{client.masked_pan}</InfoRow>}
                            {client.subsribed_at && (
                                <InfoRow label={t('clientPage.subscribedAt')}>
                                    {formatDate(client.subsribed_at)}
                                </InfoRow>
                            )}
                            {client.is_subscriber && (
                                <InfoRow label={t('clientPage.subscriptionsCost')}>
                                    {`${client.total_subscriptions_regular_amount} руб.`}
                                </InfoRow>
                            )}
                            {client.is_subscriber && (
                                <InfoRow label={t('clientPage.nextPayment')}>
                                    {formatDate(client.next_payment_planned_at)}
                                </InfoRow>
                            )}
                        </div>
                    </FormGroup>
                </section>

                <section>
                    <FormGroup title={t('clientPage.subscriptions.title')}>
                        <div className={styles.chipsHeader}>
                            <Chips
                                options={subscriptionTypes}
                                onChange={handleSubscriptionTypeChange}
                                defaultValue={subscriptionType}
                            />
                        </div>
                        {subscriptionsToRender.length > 0 ? (
                            <div className={styles.subscriptions}>{subscriptionsToRender}</div>
                        ) : (
                            <ContentFooter>Нет ни одной подписки.</ContentFooter>
                        )}
                    </FormGroup>
                </section>

                {paymentsQuery.isSuccess && (
                    <section className={styles.paymentHistory}>
                        <FormGroup title={t('clientPage.paymentsHistory.title')}>
                            <div className={styles.chipsHeader}>
                                <Chips
                                    options={paymentTypes}
                                    onChange={handlePaymentTypeChange}
                                    defaultValue={paymentType}
                                />
                            </div>
                            <div className={styles.paymentItems}>{payments}</div>
                        </FormGroup>
                        <ContentFooter>{`${paymentsCountWord} ${declOfNum(payments.length, [
                            'платёж',
                            'платежа',
                            'платежей',
                        ])}`}</ContentFooter>
                    </section>
                )}
            </Panel>

            {edit && (
                <EditClient
                    panelWidth={SUB_PANEL_WIDTH}
                    data={client}
                    onUpdated={updateClientData}
                    onBackClick={handleEditBackClick}
                />
            )}
            {getSubscriptionToEdit() !== undefined && (
                <EditSubscription
                    data={getSubscriptionToEdit()}
                    panelWidth={SUB_PANEL_WIDTH}
                    onBackClick={handleEditBackClick}
                />
            )}
        </>
    );
}
